import { api } from "@/api/utils";
import { ChartBox, formatAxisNumber, useContainerSize } from "@/components/charts/ChartBox";
import { Input } from "@/components/ui/Input";
import { getTokenCurrencyCodes, TokenCurrencyCode, tokenCurrencyToMoneyCurrency } from "@/modules/money-fns";
import { Box, Checkbox, Field, Flex, Label, Select, Separator } from "@givenwell/components";
import { colors } from "@givenwell/theme";
import { defineChart, rect, ruleY } from "@tanstack/charts";
import { decorative } from "@tanstack/charts/mark/decorative";
import { Chart } from "@tanstack/charts/react";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { scaleUtc } from "d3-scale";
import { useMemo, useState } from "react";

export const Route = createFileRoute("/dashboard/spending")({
  component: Page,
});

type SpendingQueryOptions = {
  tokenCurrency: TokenCurrencyCode;
  startDate: Date;
  endDate: Date;
  movingAverageIntervalDays: number;
};

function localToUTC(date: Date) {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000);
}

type SpendingReportRow = {
  date: Date;
  tokenCurrency: string;
  currency: string;
  type: "marketplace" | "eap" | "claims";
  dollars: number;
  count: number;
  dollarsPerCount: number;
};

function toDateOnlyString(date: Date) {
  return localToUTC(date).toISOString().split("T")[0]!;
}

function getMovingAverageDateRange(date: Date, intervalDays: number) {
  const dates = [toDateOnlyString(date)];
  for (let i = 1; i < intervalDays; i++) {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() - i);
    dates.push(toDateOnlyString(newDate));
  }
  return dates;
}

const sum = (values: number[]) => values.reduce((acc, val) => acc + val, 0);

const spendingQuery = (opts: SpendingQueryOptions) => {
  const lookbackStartDate = new Date(opts.startDate);
  lookbackStartDate.setDate(lookbackStartDate.getDate() - opts.movingAverageIntervalDays + 1);
  const lookbackStartDateString = toDateOnlyString(lookbackStartDate);
  const endDateString = toDateOnlyString(opts.endDate);
  return queryOptions({
    queryKey: [
      "spending-report",
      {
        startDate: lookbackStartDateString,
        endDate: endDateString,
      },
    ],
    queryFn: async () => {
      return await api.reporting.getManagementReportingObsSpending({
        startDate: lookbackStartDateString,
        endDate: endDateString,
      });
    },
    select(data) {
      const rows: SpendingReportRow[] = [];
      if (!getTokenCurrencyCodes().includes(opts.tokenCurrency)) return [];
      const currency = tokenCurrencyToMoneyCurrency(opts.tokenCurrency);

      const cursor: Date = new Date(opts.startDate);
      cursor.setHours(0, 0, 0, 0);
      const end: Date = new Date(opts.endDate);
      end.setHours(0, 0, 0, 0);

      while (cursor <= end) {
        const templateRow: SpendingReportRow = {
          date: localToUTC(cursor),
          tokenCurrency: opts.tokenCurrency,
          currency: currency,
          type: "marketplace",
          dollars: 0,
          count: 0,
          dollarsPerCount: 0,
        };

        const movingAverageDates = getMovingAverageDateRange(cursor, opts.movingAverageIntervalDays);

        const purchaseDataInRange = data.marketplace.filter(
          row => row.currency_code.toUpperCase() === currency && movingAverageDates.includes(row.spend_date),
        );

        const marketplaceData = purchaseDataInRange.filter(row => !row.eap_available);

        const marketplaceRow: SpendingReportRow = {
          ...templateRow,
          type: "marketplace",
          dollars: sum(marketplaceData.map(d => d.amount)) / opts.movingAverageIntervalDays,
          count: sum(marketplaceData.map(d => d.purchase_count)) / opts.movingAverageIntervalDays,
          dollarsPerCount: sum(marketplaceData.map(d => d.amount)) / sum(marketplaceData.map(d => d.purchase_count)),
        };
        const eapData = purchaseDataInRange.filter(row => row.eap_available);
        const eapRow: SpendingReportRow = {
          ...templateRow,
          type: "eap",
          dollars: sum(eapData.map(d => d.amount)) / opts.movingAverageIntervalDays,
          count: sum(eapData.map(d => d.purchase_count)) / opts.movingAverageIntervalDays,
          dollarsPerCount: sum(eapData.map(d => d.amount)) / sum(eapData.map(d => d.purchase_count)),
        };

        const claimsDataInRange = data.claims.filter(
          row => row.currency_code.toUpperCase() === currency && movingAverageDates.includes(row.spend_date),
        );
        const claimsRow: SpendingReportRow = {
          ...templateRow,
          type: "claims",
          dollars: sum(claimsDataInRange.map(d => d.approved_amount)) / opts.movingAverageIntervalDays,
          count: sum(claimsDataInRange.map(d => d.claim_count)) / opts.movingAverageIntervalDays,
          dollarsPerCount:
            sum(claimsDataInRange.map(d => d.approved_amount)) / sum(claimsDataInRange.map(d => d.claim_count)),
        };
        rows.push(marketplaceRow);
        rows.push(eapRow);
        rows.push(claimsRow);

        cursor.setDate(cursor.getDate() + 1);
      }

      return rows;
    },
    placeholderData: prev => prev,
  });
};

type SpendingMetric = "dollars" | "count" | "dollarsPerCount";
type SpendingInterval = "day" | "week" | "month";
type SpendingType = SpendingReportRow["type"];

// Stack order (bottom -> top) and fill colours, matching the Legend below.
const SPENDING_TYPE_ORDER: SpendingType[] = ["marketplace", "eap", "claims"];
const SPENDING_TYPE_COLOR: Record<SpendingType, string> = {
  marketplace: colors.cyan,
  eap: colors.green,
  claims: colors.pink,
};

// One painted rectangle: an x interval (the day/week/month bucket) and a y interval
// (stacked endpoints, or 0 -> value in overlap mode).
type SpendingRect = {
  start: Date;
  end: Date;
  type: SpendingType;
  y1: number;
  y2: number;
};

// Report dates are already `localToUTC`'d to UTC midnight, so bucket in UTC.
// Weeks start on Sunday, matching the old Plot "week" (d3 utcWeek) interval.
function bucketStart(date: Date, interval: SpendingInterval): Date {
  if (interval === "month") {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
  }
  const start = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  if (interval === "week") {
    start.setUTCDate(start.getUTCDate() - start.getUTCDay());
  }
  return start;
}

function bucketEnd(start: Date, interval: SpendingInterval): Date {
  const end = new Date(start);
  if (interval === "day") {
    end.setUTCDate(end.getUTCDate() + 1);
  } else if (interval === "week") {
    end.setUTCDate(end.getUTCDate() + 7);
  } else {
    end.setUTCMonth(end.getUTCMonth() + 1);
  }
  return end;
}

function Page() {
  const end = new Date();
  // const start = new Date(end);
  // start.setMonth(start.getMonth() - 8);
  // start.setDate(1);
  // start.setHours(0, 0, 0, 0);
  const start = new Date(2025, 0, 1, 0, 0, 0, 0);

  const [spendingQueryOptions, setQueryOptions] = useState<SpendingQueryOptions>({
    tokenCurrency: "nztoken",
    startDate: start,
    endDate: end,
    movingAverageIntervalDays: 56,
  });
  const tokenCurrency = spendingQueryOptions.tokenCurrency;

  const tokenCurrencies = getTokenCurrencyCodes();
  const { data, isFetching } = useQuery(
    spendingQuery({
      ...spendingQueryOptions,
      movingAverageIntervalDays: Math.max(1, spendingQueryOptions.movingAverageIntervalDays),
    }),
  );

  const [normalized, setNormalized] = useState(true);
  const [yAxis, setYAxis] = useState<SpendingMetric>("dollars");
  const [interval, setInterval] = useState<SpendingInterval>("day");

  const overlapRects = yAxis === "dollarsPerCount" && !normalized;

  const rects = useMemo<SpendingRect[]>(() => {
    const rows = data ?? [];

    if (overlapRects) {
      // "Avg. value of purchase", un-normalized: every per-day value is painted from
      // zero over the full bucket width, semi-transparent, overlapping its siblings.
      return rows
        .filter(row => Number.isFinite(row[yAxis]))
        .map(row => {
          const bucket = bucketStart(row.date, interval);
          return {
            start: bucket,
            end: bucketEnd(bucket, interval),
            type: row.type,
            y1: 0,
            y2: row[yAxis],
          };
        });
    }

    // Stacked: sum the metric per (bucket, type), then stack the types in order,
    // optionally normalizing each bucket to its total.
    const buckets = new Map<number, { start: Date; totals: Map<SpendingType, number> }>();
    for (const row of rows) {
      const value = row[yAxis];
      if (!Number.isFinite(value)) continue;
      const start = bucketStart(row.date, interval);
      const key = start.getTime();
      let bucket = buckets.get(key);
      if (!bucket) {
        bucket = { start, totals: new Map() };
        buckets.set(key, bucket);
      }
      bucket.totals.set(row.type, (bucket.totals.get(row.type) ?? 0) + value);
    }

    const result: SpendingRect[] = [];
    for (const bucket of buckets.values()) {
      const end = bucketEnd(bucket.start, interval);
      const total = sum(SPENDING_TYPE_ORDER.map(type => bucket.totals.get(type) ?? 0));
      let y = 0;
      for (const type of SPENDING_TYPE_ORDER) {
        const value = bucket.totals.get(type) ?? 0;
        const height = normalized ? (total > 0 ? value / total : 0) : value;
        result.push({ start: bucket.start, end, type, y1: y, y2: y + height });
        y += height;
      }
    }
    return result;
  }, [data, interval, yAxis, normalized, overlapRects]);

  const definition = useMemo(() => {
    return defineChart({
      marks: [
        // The chart has no tooltips or clicks, so the bars are purely decorative.
        decorative(
          rect(rects, {
            id: "spending",
            x1: "start",
            x2: "end",
            y1: "y1",
            y2: "y2",
            color: "type",
            fillOpacity: overlapRects ? 0.7 : 1,
            inset: 0,
          }),
        ),
        // Emphasized gridlines in normalized mode (the old custom gridY marks).
        ...(normalized
          ? [
              ruleY([0.1, 0.2, 0.3, 0.4, 0.6, 0.7, 0.8, 0.9], {
                stroke: colors.text4,
                strokeOpacity: 1,
              }),
              ruleY([0.5, 0.8], { stroke: colors.text2, strokeOpacity: 1 }),
            ]
          : []),
      ],
      scales: {
        x: {
          scale: scaleUtc,
          axis: {
            ticks: { spacing: 100, size: 0 },
            tickLabels: { fontSize: 12 },
          },
        },
        y: {
          scale: scaleLinear,
          grid: true,
          axis: {
            ticks: {
              count: 10,
              size: 0,
              format: normalized ? (value: number) => `${Math.round(value * 100)}%` : formatAxisNumber,
            },
            tickLabels: { fontSize: 12 },
          },
        },
      },
      margin: { left: 60, bottom: 40 },
      color: {
        domain: SPENDING_TYPE_ORDER,
        range: SPENDING_TYPE_ORDER.map(type => SPENDING_TYPE_COLOR[type]),
      },
    });
  }, [rects, normalized, overlapRects]);

  const { ref, height } = useContainerSize();

  return (
    <Box css={{ height: "100%", display: "flex", gap: 16 }}>
      <Box css={{ backgroundColor: "white", padding: 16, borderRadius: 16, width: 250, spaceY: 16 }}>
        <Select
          value={tokenCurrency}
          onValueChange={t => setQueryOptions({ ...spendingQueryOptions, tokenCurrency: t as TokenCurrencyCode })}
        >
          {tokenCurrencies.map(code => (
            <option key={code} value={code}>
              {tokenCurrencyToMoneyCurrency(code)}
            </option>
          ))}
        </Select>
        <Field>
          <Label>Y-Axis</Label>
          <Select value={yAxis} onValueChange={t => setYAxis(t === "count" || t === "dollarsPerCount" ? t : "dollars")}>
            <option value="dollars">Dollars</option>
            <option value="count">Count</option>
            <option value="dollarsPerCount">Avg. value of purchase</option>
          </Select>
        </Field>
        <Field css={{ flexDirection: "row" }}>
          <Label css={{ flex: "1 0 0px" }}>Normalize</Label>
          <Checkbox checked={normalized} onCheckedChange={setNormalized} />
        </Field>
        <Separator />
        <Field>
          <Label>Moving Average Window (Days)</Label>
          <Input
            type="number"
            value={spendingQueryOptions.movingAverageIntervalDays}
            min={0}
            max={56}
            step={7}
            onChange={e =>
              setQueryOptions({
                ...spendingQueryOptions,
                movingAverageIntervalDays: Number(e.target.value),
              })
            }
          />
        </Field>
        <Field>
          <Label>Group by</Label>
          <Select value={interval} onValueChange={t => setInterval(t === "week" || t === "month" ? t : "day")}>
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
          </Select>
        </Field>
        {isFetching && <Box>Loading...</Box>}
      </Box>
      <Box
        css={{
          flex: "1 0 0px",
          display: "flex",
          flexDirection: "column",
          backgroundColor: colors.white,
          borderRadius: 16,
          pt: 16,
        }}
      >
        <Legend />
        <Box css={{ flex: "1 0 0px", position: "relative" }}>
          <ChartBox ref={ref} css={{ width: "100%", height: "100%", overflow: "hidden" }}>
            {height !== undefined && (
              <Chart definition={definition} height={height} ariaLabel="Spending by type over time" />
            )}
          </ChartBox>
        </Box>
      </Box>
    </Box>
  );
}

const LEGEND_ITEMS = [
  {
    label: "Marketplace",
    color: colors.cyan,
  },
  {
    label: "Claims",
    color: colors.pink,
  },
  {
    label: "EAP",
    color: colors.green,
  },
];

function Legend() {
  return (
    <Flex css={{ gap: 24, pl: 60 }}>
      {LEGEND_ITEMS.map(item => (
        <Box key={item.label} css={{ display: "flex", alignItems: "center", fontScale: "sm" }}>
          <Box css={{ size: 20, borderRadius: 6, backgroundColor: item.color, mr: 8 }} />
          {item.label}
        </Box>
      ))}
    </Flex>
  );
}
